import { combineLatest, firstValueFrom, map, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import {
	deleteEntities,
	getActiveId,
	getAllEntities,
	getEntitiesCountByPredicate,
	getEntity,
	selectEntities,
	selectEntitiesCountByPredicate,
	selectEntity,
	selectMany,
	selectManyByPredicate,
	updateAllEntities,
	updateEntities,
	upsertEntities,
} from '@ngneat/elf-entities';
import { GUEST_TYPES } from '@pages/manifests/constants';

import { BaseRepository } from '../base.repository';
import { ManifestsRepository } from './manifests.repository';
import { Guest, guestsEntitiesRef, Product, productsEntitiesRef, store } from './manifests.store';
import { ProductsRepository } from './products.repository';

const removeDuplicates = (val): any[] => [...new Set(val)];

@Injectable({ providedIn: 'root' })
export class GuestsRepository extends BaseRepository {
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - constructor
	 **-------------------------------------------------------------------------------------
	 */
	constructor(
		private readonly productsRepo: ProductsRepository,
		private readonly manifestsRepo: ManifestsRepository
	) {
		super();
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - store
	 **-------------------------------------------------------------------------------------
	 */
	get store() {
		return store;
	}

	//selects
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */
	guests$ = store.pipe(selectEntities({ ref: guestsEntitiesRef }));
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - guestsCounts
	 **-------------------------------------------------------------------------------------
	 */
	guestsCount$ = () => {
		const { guests } = this.manifestsRepo.getActiveManifest();
		return store.pipe(selectEntitiesCountByPredicate(e => guests.includes(e.id), { ref: guestsEntitiesRef }));
	};
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */
	guest$ = id => store.pipe(selectEntity(id, { ref: guestsEntitiesRef }));

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - get
	 **-------------------------------------------------------------------------------------
	 */
	primaryGuest$(): Observable<any> {
		const { guests } = this.manifestsRepo.getActiveManifest();
		return store.pipe(
			selectManyByPredicate(({ id, isComplete, manifestGuestTypeId }) => guests.includes(id) && manifestGuestTypeId === 1, {
				ref: guestsEntitiesRef,
			})
		);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */
	completedGuests = async () => {
		const { guests } = this.manifestsRepo.getActiveManifest();
		const selectedGuests = await firstValueFrom(
			store.pipe(
				selectMany(guests, {
					ref: guestsEntitiesRef,
				})
			)
		);
		return selectedGuests.filter(e => e.isComplete);
	};
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */
	completedProductGuests = async () => {
		const guests = await this.completedGuests();
		const { activeProductId } = this.manifestsRepo.getActiveManifest();
		const product = this.productsRepo.product(activeProductId);
		return guests.filter(e => product.manifestGuests.includes(e.id));
	};

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getGuestbyProduct
	 **-------------------------------------------------------------------------------------
	 */
	public getGuests(ids: number[]): Guest[] {
		const guests = store.query(getAllEntities({ ref: guestsEntitiesRef }));
		return guests.filter(e => ids.includes(e.id));
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getGuestbyProduct
	 **-------------------------------------------------------------------------------------
	 */
	public getGuests$(ids: number[]): Observable<Guest[]> {
		return store.pipe(
			selectManyByPredicate(
				({ id }) => {
					return ids.includes(id);
				},
				{ ref: guestsEntitiesRef }
			)
		);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */
	completedGuests$ = () => {
		const { guests } = this.manifestsRepo.getActiveManifest();
		return store.pipe(
			selectManyByPredicate(({ id, isComplete }) => guests.includes(id) && !isComplete, {
				ref: guestsEntitiesRef,
			})
		);
	};
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */
	inCompletedGuests$ = store.pipe(selectManyByPredicate(({ isComplete }) => !isComplete, { ref: guestsEntitiesRef }));

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */
	selectGuests$ = guests => store.pipe(selectMany(guests, { ref: guestsEntitiesRef }));
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */
	selectMinors$ = (id, isCompleted = false) =>
		store.pipe(
			selectManyByPredicate(
				({ isMinor, guardianManifestGuestId, isComplete }) => {
					return guardianManifestGuestId == id;
				},
				{
					ref: guestsEntitiesRef,
				}
			)
		);

	//queres
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */
	activeId = () => store.query(getActiveId);
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */
	getMinorsCount = id =>
		store.query(
			getEntitiesCountByPredicate(({ isMinor, guardianManifestGuestId }) => isMinor && guardianManifestGuestId === id, {
				ref: guestsEntitiesRef,
			})
		);
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */

	getCompletedGuestCount = store.query(
		getEntitiesCountByPredicate(({ isComplete }) => isComplete, {
			ref: guestsEntitiesRef,
		})
	);
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */

	getTotalGuestCount = store.query(getAllEntities({ ref: guestsEntitiesRef })).length;
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */
	getMinors = id => firstValueFrom(this.selectMinors$(id));

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */
	updateGuest = (id: number, guest: Partial<Guest>) => {
		const record: Guest = store.query(getEntity(id, { ref: guestsEntitiesRef }));
		guest.isComplete = false;
		if (record?.isDocumentsCompleted && record?.licensePhotoProvided && record?.isVettingQuestionsCompleted) {
			guest.isComplete = true;
		}
		
		store.update(updateEntities(id, { ...guest }, { ref: guestsEntitiesRef }));
	};
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */
	updateAllGuest = (guest: Partial<Guest>) => {
		store.update(updateAllEntities(e => ({ ...e, ...guest }), { ref: guestsEntitiesRef }));
	};

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - addGuest
	 **-------------------------------------------------------------------------------------
	 */
	public async saveGuest(guest: Guest) {
		const manifest = store.query(getEntity(this.activeId()));
		const product: Product = await firstValueFrom(this.productsRepo.product$(manifest.activeProductId || guest.manifestProductId));
		store.update(
			upsertEntities(guest, { ref: guestsEntitiesRef }),
			updateEntities(
				product?.id,
				entity => ({
					...entity,
					manifestGuests: removeDuplicates([...entity?.manifestGuests, guest.id]),
				}),
				{ ref: productsEntitiesRef }
			),
			updateEntities(manifest.id, entity => ({
				...entity,
				guests: removeDuplicates([...entity.guests, guest.id]),
			}))
		);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - unAssignGuest
	 **-------------------------------------------------------------------------------------
	 */
	public async unAssignGuest(guest: Guest, product: Product) {
		this.updateGuest(guest.id, {
			manifestProductId: 0,
			acceptedAcknowledgement: null,
			acceptedWaiver: null,
			acceptedNavigationRules: null,
			acceptedRentalAgreement: null,
		});
		store.update(
			updateEntities(
				product.id,
				entity => ({
					...entity,
					manifestGuests: removeDuplicates([...entity.manifestGuests.filter(e => e !== guest.id)]),
				}),
				{ ref: productsEntitiesRef }
			)
		);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - removeGuest
	 **-------------------------------------------------------------------------------------
	 */
	public async removeGuest(id: number) {
		const minors = await this.getMinors(id);
		const idsToRemoveIncludingMinors = [id, ...minors.map(e => e.id)];

		store.update(
			deleteEntities(idsToRemoveIncludingMinors, { ref: guestsEntitiesRef }),
			updateEntities(this.activeId(), entity => ({
				...entity,
				guests: entity.guests.filter(e => !idsToRemoveIncludingMinors.includes(e)),
			}))
		);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getGuest
	 **-------------------------------------------------------------------------------------
	 */
	public async getGuest(id: number) {
		return await firstValueFrom(store.pipe(selectEntity(id, { ref: guestsEntitiesRef })));
	}
	
	public getGuestSync(id:number) {
		return store.query(getEntity(id, { ref: guestsEntitiesRef }));
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getAssignedGuests
	 **-------------------------------------------------------------------------------------
	 */
	public getUnassignedGuests(manifestId) {
		const { products, id } = this.manifestsRepo.getActiveManifest();
		return combineLatest({
			products: store.pipe(
				selectMany(products, { ref: productsEntitiesRef }),
				map(e => e.map(i => i.manifestGuests))
			),
			guests: store.pipe(
				selectEntity(manifestId),
				map(e => e?.guests)
			),
		}).pipe(
			switchMap(e => {
				const products = e.products.flat();
				const guests = e.guests.filter(id => {
					return !products.includes(id);
				});
				return store.pipe(selectMany(guests, { ref: guestsEntitiesRef }));
			})
		);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getMinorGaurdian
	 **-------------------------------------------------------------------------------------
	 */
	public getMinorGaurdian(id: number): Guest {
		const guest = store.query(getEntity(id, { ref: guestsEntitiesRef }));
		if (guest?.isMinor) {
			return store.query(getEntity(guest.guardianManifestGuestId, { ref: guestsEntitiesRef }));
		}
		return <Guest>{};
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - markGuestAsComplete
	 **-------------------------------------------------------------------------------------
	 */
	public markGuestAsComplete(guest): Boolean {
		//const guest: Guest = store.query(getEntity(id, { ref: guestsEntitiesRef }));
		const id = guest.id;
		if ('checkinCompleted' in guest) {
			store.update(updateEntities(id, { ...guest, isComplete: guest.checkinCompleted }, { ref: guestsEntitiesRef }));
			return true;
		}
		if ((guest?.signatureProvided || guest?.signature) && guest?.isMinor) {
			//minor check
			store.update(updateEntities(id, { ...guest, isComplete: true }, { ref: guestsEntitiesRef }));
			return true;
		}

		if (guest.manifestGuestTypeId === GUEST_TYPES.guest && guest?.signatureProvided && (guest?.signatureProvided || guest?.signature)) {
			store.update(updateEntities(id, { ...guest, isComplete: true }, { ref: guestsEntitiesRef }));
			return true;
		}

		if ((guest?.signatureProvided || guest?.signature) && guest?.licensePhotoProvided && guest?.email) {
			store.update(updateEntities(id, { ...guest, isComplete: true }, { ref: guestsEntitiesRef }));
			return true;
		}

		return false;
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - markGuestsAsComplete
	 **-------------------------------------------------------------------------------------
	 */
	public markGuestsAsComplete(guests): void {
		guests.forEach(e => this.markGuestAsComplete(e));
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - allDocumentsSigned
	 **-------------------------------------------------------------------------------------
	 */
	public async allGuestDocumentsSigned(guestId: number): Promise<boolean> {
		const guest = await this.getGuest(guestId);
		const minors = await this.getMinors(guestId);
		return guest.signatureProvided && minors.every(e => e.signatureProvided);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - allDocumentsSigned
	 **-------------------------------------------------------------------------------------
	 */
	public async signatureCount(guestId: number): Promise<number> {
		const guest = await this.getGuest(guestId);
		const minors = await this.getMinors(guestId);
		return Number(guest.signatureProvided) + minors.filter(e => e.signatureProvided).length;
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - allDocumentsSigned
	 **-------------------------------------------------------------------------------------
	 */
	public isGuestDocumentsSigned(guestId: number): boolean {
		const guest = store.query(getEntity(guestId, { ref: guestsEntitiesRef }));
		return guest.signatureProvided;
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getGuestSteps
	 **-------------------------------------------------------------------------------------
	 */
	public getGuestSteps(guestId: number): string[] {
		const guest = store.query(getEntity(guestId, { ref: guestsEntitiesRef }));
		const { checkInEnabled } = this.manifestsRepo.getActiveManifest();
		if (guest?.isMinor) return ['Form', 'Docs'];
		if (!checkInEnabled) return ['Form', 'Minors', 'Docs'];
		if (guest?.manifestGuestTypeId === GUEST_TYPES.guest) return ['Form', 'Minors', 'Docs'];
		return ['Form', 'Minors', 'Docs', 'License'];
	}

	/*
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 ** METHOD NAME - getGuestPageHeader
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 */
	public getGuestPageHeader(id): string {
		const guest: Guest = store.query(getEntity(id, { ref: guestsEntitiesRef }));
		if ([GUEST_TYPES.operator, GUEST_TYPES.additionalOperator, GUEST_TYPES.primaryOperator].includes(guest?.manifestGuestTypeId)) {
			return 'Driver Details';
		}
		return 'Non-Driver Details';
	}
}
